import React, {useState} from "react";
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from "@headlessui/react";
import {format} from "date-fns";
import type {Competition} from "@/hooks/useCompetitions";
import {launchProviderCheckout} from "@/features/checkout/checkout.ts";
import api from "@/lib/api.ts";
import {notify} from "@/components/Notifications.tsx";


export type Props = {
    open: boolean;
    initial: Competition | null;
    onClose: () => void;
};

export default function CompetitionDetailsDialog({open, initial, onClose}: Props) {
    if (!open || !initial) return null;

    const placeholder = "https://placehold.co/1200x600";
    const start = initial.startAt ? new Date(initial.startAt) : null;
    const end = initial.endAt ? new Date(initial.endAt) : null;

    const hero =
        (initial as any).heroImageUrl ||
        (initial as any).imageUrl ||
        (initial as any).media?.url ||
        placeholder;

    const prizes: string[] = Array.isArray((initial as any)?.prizes)
        ? ((initial as any).prizes as string[]).filter(Boolean)
        : [];

    return (
        <Dialog open={open} onClose={onClose} className="relative z-10">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-800/75 data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in"
            />
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
                    <DialogPanel
                        transition
                        className="relative w-full max-w-3xl transform overflow-hidden rounded-xl bg-white shadow-xl transition-all
              data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out
              sm:data-[closed]:translate-y-0 sm:data-[closed]:scale-95"
                    >
                        <div className="relative bg-gray-50">
                            {initial.targetUrl ? (
                                <a href={initial.targetUrl} target="_blank" rel="noopener noreferrer">
                                    <img src={hero} alt={initial.title} className="h-full w-full object-cover"/>
                                </a>
                            ) : (
                                <img src={hero} alt={initial.title} className="h-full w-full object-cover"/>
                            )}
                        </div>

                        <div className="p-5 sm:p-6">
                            <DialogTitle as="h3" className="text-lg font-semibold text-blue-900">
                                {initial.title}
                            </DialogTitle>

                            {initial.description && (
                                <p className="mt-3 whitespace-pre-line text-sm text-gray-700">
                                    {initial.description}
                                </p>
                            )}

                            <dl className="mt-6 grid gap-4 sm:grid-cols-2">
                                <div>
                                    <dt className="text-xs font-medium text-gray-500">Begin</dt>
                                    <dd className="text-sm text-blue-900">
                                        {start ? format(start, "MMM d, yyyy HH:mm") : "—"}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-xs font-medium text-gray-500">Einde</dt>
                                    <dd className="text-sm text-blue-900">
                                        {end ? format(end, "MMM d, yyyy HH:mm") : "—"}
                                    </dd>
                                </div>
                            </dl>

                            {prizes.length > 0 && (
                                <div className="mt-6">
                                    <h4 className="text-sm font-semibold text-blue-900">Pryse</h4>
                                    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700">
                                        {prizes.map((p, i) => (
                                            <li key={i}>{p}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <Actions
                                onClose={onClose}
                                competitionId={initial.id}
                                status={initial.status}
                                phone={"264851273000"}
                                keyword={initial.code}
                                entryFee={initial.entryFee}
                            />
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}

type ActionProps = {
    onClose: () => void;
    competitionId: string;
    status?: string;
    phone: string;     // e.g. "+264 81 234 5678"
    keyword?: string;  // e.g. "Kodewoord: KOSMOS"
    entryFee?: number; // e.g. 10
};

function buildWhatsAppUrl(phone: string, text: string) {
    const digitsOnly = phone.replace(/[^\d]/g, "");
    const msg = encodeURIComponent(text);
    return `https://wa.me/${digitsOnly}?text=${msg}`;
}

export function Actions({onClose, competitionId, status, phone, keyword, entryFee}: ActionProps) {
    const [showForm, setShowForm] = useState(false);
    const [quantity, setQuantity] = useState(1);
    const [submitting, setSubmitting] = useState(false);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [errors, setErrors] = useState<{
        name?: string;
        email?: string;
        phone?: string;
        quantity?: string;
    }>({});

    const isOpen = status === "OPEN";
    const hasKeyword = !!keyword && keyword.trim().length > 0;
    const hasEntryFee = typeof entryFee === "number" && entryFee > 0;

    const waHref = hasKeyword ? buildWhatsAppUrl(phone, keyword!) : undefined;

    const chipOptions = [1, 3, 5, 10, 25];

    const total = hasEntryFee ? (quantity > 0 ? quantity * entryFee! : 0) : 0;

    function validate(): boolean {
        const next: typeof errors = {};

        if (!name.trim()) next.name = "Voer asseblief jou naam in";
        if (!email.trim()) next.email = "Voer asseblief jou e-posadres in";
        if (!phoneNumber.trim()) next.phone = "Voer asseblief u telefoonnommer in";
        if (!quantity || quantity <= 0) next.quantity = "Voer asseblief 'n geldige aantal in";

        setErrors(next);
        return Object.keys(next).length === 0;
    }

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        const payload = {
            facilitator: 'FINATIC',
            name: name.trim(),
            email: email.trim(),
            phone: phoneNumber.trim(),
            quantity,
        };

        try {
            setSubmitting(true);
            const {data} = await api.post("/api/v1/public/competitions/" + competitionId + "/entries", payload);
            launchProviderCheckout(data);
        } catch (err: any) {
            notify.error("Failed to submit. Please try again.");
        } finally {
            setSubmitting(false);
        }

    }

    return (
        <div className="mt-6">
            {/* Primary actions row */}
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                {/* Close */}
                <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-60"
                >
                    Maak toe
                </button>

                {/* Send keyword via WhatsApp */}
                {hasKeyword && isOpen && waHref && (
                    <a
                        href={waHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 bg-emerald-600 hover:bg-emerald-500"
                    >
                        <svg viewBox="0 0 32 32" aria-hidden="true" className="mr-2 h-4 w-4">
                            <path
                                d="M27.6 4.4A14 14 0 0 0 3.9 24.6L2 30l5.6-1.8A14 14 0 0 0 28 5.9c-.1-.5-.2-1-.4-1.5zM16 26.5c-2.2 0-4.3-.6-6.1-1.8l-.4-.2-3.6 1.1 1.2-3.5-.3-.5A10.4 10.4 0 1 1 26.5 16 10.5 10.5 0 0 1 16 26.5zm5.9-7.7c-.3-.2-1.9-1-2.2-1.2-.3-.1-.5-.2-.7.2-.2.3-.8 1.2-1 1.4-.2.2-.4.2-.7.1s-1.3-.5-2.5-1.6c-.9-.8-1.6-1.8-1.8-2.1-.2-.3 0-.5.1-.7.1-.1.3-.4.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5 0-.2-.7-1.8-1-2.5-.3-.7-.5-.6-.7-.6h-.6c-.2 0-.5.1-.7.4s-1 1-1 2.5 1 2.9 1.1 3.1c.1.2 2 3.2 4.7 4.5 2.9 1.4 2.9.9 3.4.9.5 0 1.7-.7 2-1.4.3-.7.3-1.3.2-1.4-.1-.1-.3-.2-.6-.4z"
                                fill="currentColor"
                            />
                        </svg>
                        Stuur Kodewoord (WhatsApp)
                    </a>
                )}

                {/* Enter competition button (opens form) */}
                {hasEntryFee && isOpen && (
                    <button
                        type="button"
                        onClick={() => setShowForm((prev) => !prev)}
                        className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 bg-blue-600 hover:bg-blue-500"
                    >
                        {showForm
                            ? "Maak Vorm toe"
                            : `Neem Deel (N$ ${entryFee!.toFixed(2)} per deelname)`}
                    </button>
                )}
            </div>

            {/* Form – mirrors Flutter bottom sheet content */}
            {hasEntryFee && isOpen && showForm && (
                <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4 sm:p-5">
                    <h4 className="text-base font-semibold text-gray-900">
                        Neem Deel Aan Kompetisie
                    </h4>
                    <p className="mt-1 text-sm text-gray-600">
                        Vul asseblief die vorm hieronder in om deel te neem.
                    </p>

                    {/* Quantity selection */}
                    <div className="mt-4">
                        <p className="text-sm font-medium text-gray-900">
                            Kies Aantal Deelname
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {chipOptions.map((n) => (
                                <button
                                    key={n}
                                    type="button"
                                    onClick={() => {
                                        setQuantity(n);
                                        setErrors((prev) => ({...prev, quantity: undefined}));
                                    }}
                                    className={[
                                        "rounded-full border px-3 py-1 text-sm",
                                        quantity === n
                                            ? "bg-blue-600 text-white border-blue-600"
                                            : "bg-white text-gray-800 border-gray-300",
                                    ].join(" ")}
                                >
                                    {n}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700">
                            Aantal deelname
                        </label>
                        <input
                            type="number"
                            min={1}
                            value={quantity}
                            onChange={(e) => {
                                const parsed = parseInt(e.target.value || "1", 10);
                                setQuantity(Number.isNaN(parsed) ? 1 : parsed);
                                setErrors((prev) => ({...prev, quantity: undefined}));
                            }}
                            className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                        />
                        {errors.quantity && (
                            <p className="mt-1 text-xs text-red-600">
                                {errors.quantity}
                            </p>
                        )}
                    </div>

                    <div className="my-4 border-t border-gray-200"/>

                    {/* Details form */}
                    <form onSubmit={submit} className="space-y-3">
                        <p className="text-sm font-semibold text-gray-900">
                            Jou Besonderhede
                        </p>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                Volledige Naam
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    setErrors((prev) => ({...prev, name: undefined}));
                                }}
                                className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                            />
                            {errors.name && (
                                <p className="mt-1 text-xs text-red-600">
                                    {errors.name}
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                E-posadres
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setErrors((prev) => ({...prev, email: undefined}));
                                }}
                                className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                            />
                            {errors.email && (
                                <p className="mt-1 text-xs text-red-600">
                                    {errors.email}
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                Telefoonnommer
                            </label>
                            <input
                                type="tel"
                                value={phoneNumber}
                                onChange={(e) => {
                                    setPhoneNumber(e.target.value);
                                    setErrors((prev) => ({...prev, phone: undefined}));
                                }}
                                className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                            />
                            {errors.phone && (
                                <p className="mt-1 text-xs text-red-600">
                                    {errors.phone}
                                </p>
                            )}
                        </div>

                        {/* Total */}
                        {quantity > 0 && hasEntryFee && (
                            <div className="mt-4 border-t border-gray-200 pt-3">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="font-semibold text-gray-900">
                                        Total
                                    </span>
                                    <span className="font-semibold text-gray-900">
                                        N${total!.toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        )}

                        <div className="mt-4 flex justify-end">
                            <button
                                type="submit"
                                disabled={submitting}
                                className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                            >
                                {submitting ? "Submitting..." : "Betaal & Bevestig Deelname"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
